import axios from 'axios';
import moment from 'moment-timezone';

import Mgr from '@/core/services/security.service';

const STATUS_CODE = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  NOT_FOUND: 404,
  FORBIDDEN: 403,
  SERVER_ERROR: 500,
};

var user = new Mgr();

// Set config defaults when creating the instance
const instance = axios.create({
  baseURL: process.env.VUE_APP_API,
  withCredentials: false,
});

// Alter defaults after instance has been created
instance.defaults.headers.common['Access-Control-Allow-Origin'] = '*';
instance.defaults.headers.common['Access-Control-Allow-Credentials'] = 'true';
instance.defaults.headers.common['Content-Type'] =
  'application/json; charset=utf-8';
instance.defaults.headers.common['Accept'] = 'application/json';
instance.defaults.headers.common['X-Timezone-Offset'] = moment.tz.guess();
instance.defaults.headers.common['Device'] = 'Web';

// Add a request interceptor
instance.interceptors.request.use(
  // Do something before request is sent
  (config) => config,
  // Do something with request error
  (error) => Promise.reject(error),
);

// Add a response interceptor
instance.interceptors.response.use(
  (response) => {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    if (response.status === 200 || response.status === 201) {
      return Promise.resolve(response);
    } else {
      return Promise.reject(response);
    }
  },
  async (error) => {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')) {
      return Promise.reject({
        data: {
          error: {
            code: 'timeout',
            message: 'Yêu cầu quá thời gian chờ, vui lòng thử lại.',
          },
        },
      });
    }
    if (error.response && error.response.status) {
      switch (error.response.status) {
        // 401 là hết phiên, không phải thiếu quyền. Bản cũ gộp chung với 403 rồi trả
        // 200 giả nên không ai gia hạn token: app cứ gọi tiếp bằng token đã chết.
        case STATUS_CODE.UNAUTHORIZED: {
          const original = error.config || {};
          if (!original.__retriedAfterRenew) {
            try {
              const renewed = await user.renewToken();
              const token = renewed?.access_token;
              if (token) {
                original.__retriedAfterRenew = true;
                original.headers = {
                  ...(original.headers || {}),
                  Authorization: 'Bearer ' + token,
                };
                return instance.request(original);
              }
            } catch (renewError) {
              console.error('Renew token failed on 401:', renewError);
            }
          }

          window.dispatchEvent(
            new CustomEvent('api:session-expired', {
              detail: { message: 'Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại.' },
            }),
          );
          await user.signOut();
          return Promise.reject(error.response);
        }
        case STATUS_CODE.FORBIDDEN: {
          const msg =
            error.response.data?.error?.message ||
            error.response.data?.message ||
            'Bạn không có quyền thực hiện chức năng này';
          window.dispatchEvent(new CustomEvent('api:forbidden', { detail: { message: msg } }));
          return Promise.resolve({
            status: 200,
            data: { meta: { success: false }, data: null, error: { code: 'access_denied', message: null } },
          });
        }
      }
      return Promise.reject(error.response);
    }
    return Promise.reject(error);
  },
);

/**
 * Service to call HTTP request via Axios
 */
export default class ApiService {
  /**
   * Define header HTTP request
   * @param url
   * @param config
   * @returns {*}
   */
  async defineHeaderAxios() {
    await user.getAcessToken().then((acessToken) => {
      instance.defaults.headers.common['Authorization'] =
        'Bearer ' + acessToken;
    });
  }

  /**
   * Send the GET HTTP request
   * @param url
   * @param config
   * @returns {*}
   */
  async get(url, config = {}) {
    await this.defineHeaderAxios();
    try {
      let res = await instance.get(url, { ...config });
      if (res.status === 200 || res.status === 201) {
        if (config.responseType) {
          return res;
        }
        return res.data;
      }
    } catch (error) {
      if (error && error.data) {
        if (error.data.error) {
          throw error.data.error;
        }
        throw error.data;
      }
      throw 'Server không response';
    }
  }

  /**
   * Set the POST HTTP request
   * @param url
   * @param data
   * @param config
   * @returns {*}
   */
  async post(url, data, config = {}) {
    await this.defineHeaderAxios();
    try {
      let res = await instance.post(url, data, { ...config });
      if (res.status === 200 || res.status === 201) {
        if (config.responseType) {
          return res;
        }
        return res.data;
      }
    } catch (error) {
      if (error) {
        if (error.data) {
          if (error.data.error) {
            throw error.data.error;
          }
          throw error.data;
        }
      }
      throw { code: 'unknow', message: 'Server không response' };
    }
  }

  /**
   * Send the PUT HTTP request
   * @param url
   * @param data
   * @param config
   * @returns {*}
   */
  async put(url, data, config = {}) {
    await this.defineHeaderAxios();
    try {
      let res = await instance.put(url, data, { ...config });
      if (res.status === 200 || res.status === 201) {
        return res.data;
      }
    } catch (error) {
      if (error) {
        if (error.data) {
          if (error.data.error) {
            throw error.data.error;
          }
          throw error.data;
        }
      }
      throw { code: 'unknow', message: 'Server không response' };
    }
  }

  /**
   * Send the PATCH HTTP request
   * @param url
   * @param data
   * @param config
   * @returns {*}
   */
  async patch(url, data, config = {}) {
    await this.defineHeaderAxios();
    try {
      let res = await instance.patch(url, data, { ...config });
      if (res.status === 200 || res.status === 201) {
        return res.data;
      }
    } catch (error) {
      if (error && error.data) {
        if (error.data.error) {
          throw error.data.error;
        }
        throw error.data;
      }
      throw error;
    }
  }

  /**
   * Send the DELETE HTTP request
   * @param url
   * @param config
   * @returns {*}
   */
  async delete(url, config = {}) {
    await this.defineHeaderAxios();
    try {
      let res = await instance.delete(url, { ...config });
      if (res.status === 200 || res.status === 201) {
        return res.data;
      }
    } catch (error) {
      if (error && error.data) {
        if (error.data.error) {
          throw error.data.error;
        }
        throw error.data;
      }
      throw error;
    }
  }

  /**
   * Set the POST Firebase HTTP request
   * @param url
   * @param data
   * @param config
   * @returns {*}
   */
  async getFirebaseDynamicLinks(data) {
    try {
      let config = {
        method: 'post',
        url: 'https://firebasedynamiclinks.googleapis.com/v1/shortLinks?key=AIzaSyAphqeQ1kUwLJH4uZ0hhJjwLyrSFgqgpu8',
        headers: {
          'Content-Type': 'application/json',
        },
        data: JSON.stringify(data),
      };

      let res = await axios(config);
      if (res.status === 200 || res.status === 201) {
        return res.data && res.data.shortLink ? res.data.shortLink : '';
      }
    } catch (error) {
      if (error.response && error.response.data) {
        if (error.response.data.error) {
          throw error.response.data.error;
        }
        throw error.response.data;
      }
      throw error.response;
    }
  }
}
